import React from "react";
import { C, fmt, pct } from "./shared";

const inputCls = "w-full rounded px-2 py-1.5 text-xs font-semibold focus:outline-none text-right";
const inputStyle = { backgroundColor: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.35)", color: "#93C5FD" };

const VarBadge = ({ v }) => {
  if (v == null) return <span className="text-white/25 text-[10px]">—</span>;
  const good = v >= 0;
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: good ? "rgba(44,197,168,0.15)" : "rgba(248,113,113,0.15)", color: good ? C.green : C.red }}>
      {v >= 0 ? "+" : ""}{(v * 100).toFixed(0)}%
    </span>
  );
};

export default function Tracking({ out, model, save }) {
  const tk = out.tracking || {};
  const s = tk.summary || {};
  const rows = tk.months || [];
  const actuals = model.actuals || {};
  const upd = (key, k, v) => save({ actuals: { [key]: { ...(actuals[key] || {}), [k]: v } } });

  if (!rows.length) {
    const first = out.calendar?.month_labels?.[0];
    return (
      <div data-testid="fm-tracking" className="max-w-3xl rounded-xl p-8 text-center" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
        <div className="text-white font-extrabold">Your first check-in unlocks after {first} ends</div>
        <div className="text-xs text-white/45 mt-2 max-w-md mx-auto">
          At the end of every month, come back and log what actually happened — revenue, costs and cash. Clinq blends your actuals into the model so the forecast rolls forward and your history becomes fact.
        </div>
      </div>
    );
  }

  const num = (key, k) => {
    const v = (actuals[key] || {})[k];
    return v === null || v === undefined ? "" : v;
  };

  return (
    <div data-testid="fm-tracking" className="space-y-5">
      <div className="text-xs text-white/45 max-w-3xl">
        Monthly tracking — enter what actually happened each month. Actuals <b className="text-white/70">replace the forecast</b> for those months (rolling reforecast), and your variance against the original plan is what funders read as management discipline.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl">
        {[["Months elapsed", s.elapsed_months, undefined, "fm-track-elapsed"],
          ["Months logged", `${s.entered_months} / ${s.elapsed_months}`, s.entered_months === s.elapsed_months ? C.green : C.orange, "fm-track-logged"],
          ["YTD actual revenue", fmt(s.ytd_actual_revenue), undefined, "fm-track-actual"],
          ["Tracking vs plan", s.ytd_pct_of_forecast != null ? pct(s.ytd_pct_of_forecast) : "—",
            s.ytd_pct_of_forecast >= 1 ? C.green : s.ytd_pct_of_forecast >= 0.8 ? C.orange : C.red, "fm-track-pct"],
        ].map(([l, v, tone, tid]) => (
          <div key={l} className="rounded-lg px-4 py-3" data-testid={tid} style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{l}</div>
            <div className="text-lg font-extrabold mt-0.5" style={{ color: tone || "#fff" }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg overflow-x-auto max-w-5xl" style={{ border: "1px solid rgba(123,211,206,0.12)" }}>
        <table className="text-xs w-full" data-testid="fm-tracking-table">
          <thead>
            <tr style={{ backgroundColor: C.panel }}>
              {["Month", "Forecast revenue", "Actual revenue", "Actual COGS", "Actual opex", "Actual closing cash", "Rev vs plan"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-white/50 font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-white/5">
                <td className="px-3 py-1.5 font-bold whitespace-nowrap" style={{ color: r.entered ? C.tealSoft : "#fff" }}>{r.label}</td>
                <td className="px-3 py-1.5 text-white/60 text-right whitespace-nowrap">{fmt(r.forecast.revenue)}</td>
                {["revenue", "cogs", "opex", "closing_cash"].map((k) => (
                  <td key={k} className="px-2 py-1.5" style={{ minWidth: 110 }}>
                    <input type="number" className={inputCls} style={inputStyle}
                      data-testid={`fm-actual-${r.key}-${k}`} placeholder={k === "closing_cash" ? "optional" : "0"}
                      value={num(r.key, k)}
                      onChange={(e) => upd(r.key, k, e.target.value === "" ? null : parseFloat(e.target.value))} />
                  </td>
                ))}
                <td className="px-3 py-1.5"><VarBadge v={r.variance_pct} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-white/35 max-w-3xl">
        Logged months are blended into your P&L, cashflow and balance sheet automatically. Your refreshed funder-ready download costs 10 credits each new month.
      </div>
    </div>
  );
}
